import { showToast } from '../shared/index';

interface SpeechRecognitionEvent extends Event {
  results: {
    [index: number]: {
      [index: number]: {
        transcript: string;
      };
    };
  };
}

interface SpeechRecognitionInstance extends EventTarget {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start(): void;
  stop(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  onend: (() => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition?: { new (): SpeechRecognitionInstance };
    webkitSpeechRecognition?: { new (): SpeechRecognitionInstance };
  }
}

let activeRecognition: SpeechRecognitionInstance | null = null;
let activeBtn: HTMLElement | null = null;
let activeStopCallback: (() => void) | null = null;

export function initVoiceInput(): void {
  const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;

  document.addEventListener('click', (e: MouseEvent) => {
    const target = (e.target as HTMLElement)?.closest('#voice-btn, [data-voice-target]') as HTMLElement | null;
    if (!target) return;

    e.preventDefault();

    if (!SpeechRec) {
      showToast('Ваш браузер не поддерживает голосовой ввод (Web Speech API). Используйте Chrome или Edge.', 'error');
      return;
    }

    if (activeStopCallback) {
      const wasSameBtn = (activeBtn === target);
      activeStopCallback();
      if (wasSameBtn) return;
    }

    const targetSelector = target.dataset.voiceTarget || '#user-input';
    const inputEl = document.querySelector<HTMLInputElement | HTMLTextAreaElement>(targetSelector);
    if (!inputEl) return;

    const rec = new SpeechRec();
    rec.lang = 'ru-RU';
    rec.interimResults = true;
    rec.continuous = true;

    const initialText = inputEl.value;
    const selectionStart = inputEl.selectionStart ?? initialText.length;
    const selectionEnd = inputEl.selectionEnd ?? initialText.length;
    const prefix = initialText.slice(0, selectionStart);
    const suffix = initialText.slice(selectionEnd);

    let silenceTimer: ReturnType<typeof setTimeout> | null = null;
    let isManuallyStopped = false;
    let accumulatedTranscript = '';
    let currentSessionTranscript = '';

    function stopVoiceUI() {
      isManuallyStopped = true;
      if (silenceTimer) {
        clearTimeout(silenceTimer);
        silenceTimer = null;
      }
      if (activeBtn) {
        activeBtn.classList.remove('voice-btn--recording', 'active');
      }
      activeRecognition = null;
      activeBtn = null;
      activeStopCallback = null;
    }

    activeStopCallback = () => {
      isManuallyStopped = true;
      if (silenceTimer) {
        clearTimeout(silenceTimer);
        silenceTimer = null;
      }
      if (activeRecognition) {
        try { activeRecognition.stop(); } catch { /* stop() бросает, если распознавание уже остановлено браузером */ }
      }
      stopVoiceUI();
    };

    function resetSilenceTimer() {
      if (silenceTimer) clearTimeout(silenceTimer);
      silenceTimer = setTimeout(() => {
        if (activeStopCallback) activeStopCallback();
      }, 3000);
    }

    rec.onresult = (event: SpeechRecognitionEvent) => {
      currentSessionTranscript = '';
      for (let i = 0; i < Object.keys(event.results).length; i++) {
        const res = event.results[i];
        if (res && res[0]) {
          currentSessionTranscript += res[0].transcript;
        }
      }
      const spaceBefore = prefix.length > 0 && !/\s$/.test(prefix) && (accumulatedTranscript || currentSessionTranscript) ? ' ' : '';
      inputEl.value = prefix + spaceBefore + accumulatedTranscript + currentSessionTranscript + suffix;
      inputEl.dispatchEvent(new Event('input', { bubbles: true }));
      resetSilenceTimer();
    };

    rec.onerror = () => {
      showToast('Ошибка распознавания речи. Проверьте доступ к микрофону.', 'error');
      stopVoiceUI();
    };

    rec.onend = () => {
      if (!isManuallyStopped && silenceTimer) {
        accumulatedTranscript += currentSessionTranscript;
        currentSessionTranscript = '';
        try {
          rec.start();
          return;
        } catch {
          // Если перезапуск не удался
        }
      }
      stopVoiceUI();
    };

    activeRecognition = rec;
    activeBtn = target;
    target.classList.add('voice-btn--recording', 'active');

    resetSilenceTimer();

    try {
      rec.start();
    } catch {
      stopVoiceUI();
    }
  });
}
